import { useMemo, useState, useEffect, useReducer, useRef, memo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import moment from 'moment-timezone'
import { ChartSection } from '../chart-section/ChartSection'
import './AccountInformationSection.scss'
import accountInfoIcon from '../../../../images/charts/Accountinfo.png'
import { InfoCard } from '../info-card/InfoCard'
import { getAccountInfoConfig } from '../../../../utils/commonPatientCarts'
import {
  FIRST_NAME_TEXT,
  LAST_NAME_TEXT,
  STREET_ADDRESS_1_TEXT,
  CITY_TEXT,
  STATE_TEXT,
  ZIP_CODE_TEXT,
  ACCOUNT_INFORMATION_TEXT,
  PREFERRED_PHARMACY_TEXT,
  EMERGENCY_CONTACT_TEXT,
  INTAKE_FORM_TYPE_PHONE_NUMBER_TEXT,
  INVALID_ZIP_CODE_ERROR_TEXT,
  ACCOUNT_STATUS_TREAT_ERROR,
  EMAIL_ADDRESS_TEXT,
  profilePermissions,
} from '../../../../utils/constants'
import { modifySelectedPharmacyConfigForUpdate, setPharmacyAddressValue, validateEmail } from '../../../../utils/common'
import { updateAccountInfo } from '../../../../store/charts/ChartsThunk'
import { errorCodes, getUserFriendlyErrorMessage } from '../../../../utils/errormessages'
import { setChartsError, setIsAccountUpdatedSuccess, setPhoneValidation } from '../../../../store/charts/ChartsActions'
import { personalInfoReducer, setPersonalInfoState } from './reducers/PersonalInfoReducer'
import { contactInfoReducer, setContactInfoState } from './reducers/ContactInfoReducer'
import { emergencyInfoReducer, setEmergencyInfoState } from './reducers/EmergencyInfoReducer'
import { EditPersonalInfoBlock } from './components/edit-personal-info-block/EditPersonalInfoBlock'
import { EditContactInfoBlock } from './components/edit-contact-info-block/EditContactInfoBlock'
import { EditEmergencyInfoBlock } from './components/edit-emergency-info-block/EditEmergencyInfoBlock'
import { AccountCreditsBlock } from './components/account-credits-block/AccountCreditsBlock'
import { DATE_FORMAT_SLASH } from '../../../../utils/dateUtils'
import { compareArray } from '../../Utils'

// @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
const getPhoneNumberValue = (phoneNumberData) => {
  if (phoneNumberData) {
    return `${phoneNumberData.Number.replace(phoneNumberData.Prefix, '')}`
  }
  return ''
}

const stringHasLatterRegexp = /^[A-Za-z -]+$/g
const zipCodeRegexp = /^$|^(\d{5})(?:-\d{4})?$/

const PERSONAL_INFO_REDUCER_TYPE = 'personalInfo'
const CONTACT_INFO_REDUCER_TYPE = 'contactInfo'

const initialPersonalInfoState = {
  firstName: '',
  lastName: '',
  prefferedName: '',
  dateOfBirthValue: '',
  firstNameLocalError: '',
  lastNameLocalError: '',
  dateOfBirthLocalError: '',
  isAccountInfoChanged: false,
  initialPersonalInfo: {},
}

const initialContactInfoState = {
  isPhoneChanged: false,
  isPhoneValidation: false,
  isPhoneCorrect: false,
  isOldNumberCorrect: '',
  isAddressValid: false,
  phoneCountry: 'us',
  phoneCountryCode: '1',
  phoneNumber: '',
  streetAddress1: '',
  streetAddress2: '',
  city: '',
  stateCode: '',
  zipCode: '',
  pharmacyValue: '',
  pharmacyValueBeforeInputBlured: '',
  selectedPharmacyId: null,
  selectedPharmacyValue: '',
  selectedPharmacyConfig: null,
  phoneLocalError: '',
  streetAddress1LocalError: '',
  cityLocalError: '',
  stateCodeLocalError: '',
  zipCodeLocalError: '',
  pharmacyLocalError: '',
  forcePhoneLocalError: '',
  initialPhoneData: {},
  initialAddressData: {},
  emailAddress: '',
  emailAddressLocalError: '',
}

const initialEmergencyInfoState = {
  isPhoneChanged: false,
  firstName: '',
  lastName: '',
  relationship: '',
  isPhoneValidation: false,
  isPhoneCorrect: false,
  isOldNumberCorrect: '',
  isResetEmergencyContact: false,
  phoneCountry: 'us',
  phoneCountryCode: '1',
  phoneNumber: '',
  firstNameLocalError: '',
  lastNameLocalError: '',
  relationshipLocalError: '',
  phoneLocalError: '',
  forcePhoneLocalError: '',
  initialEmergencyData: {},
}

const emptyObject = {}

// @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
export const AccountInformationSection = memo(({ isMergedAccount = false, setIsGeneralInfoEditMode, userTimeZone }) => {
  const [searchParams] = useSearchParams()
  const id = searchParams.get('id')

  const dispatch = useDispatch()

  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const phoneIsValid = useSelector((state) => state.chartsReducer.patientInfo?.[id]?.phoneIsValid)
  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const patientInfo = useSelector((state) => state.chartsReducer.patientInfo)
  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const isAccountUpdatedSuccess = useSelector((state) => state.chartsReducer.isAccountUpdatedSuccess)
  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const profile = useSelector((state) => state.accountSettings.adminProfile)
  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const mediaFiles = useSelector((state) => state.chartsReducer?.patientInfo?.[id]?.mediaFiles || null, compareArray)

  const isCanEditEmail = profile?.Permissions?.includes(profilePermissions.CAN_EDIT_EMAIL)

  const currentPatientInfo = patientInfo?.[id] || emptyObject

  const [statePersonalInfo, dispatchPersonalInfo] = useReducer(personalInfoReducer, initialPersonalInfoState)
  const [stateContactInfo, dispatchContactInfo] = useReducer(contactInfoReducer, initialContactInfoState)
  const [stateEmergencyInfo, dispatchEmergencyInfo] = useReducer(emergencyInfoReducer, initialEmergencyInfoState)

  const [isCollapsed, setIsCollapsed] = useState(true)
  const [isEditMode, setIsEditMode] = useState(false)
  const [isPhoneValidationInProgress, setIsPhoneValidationInProgress] = useState(false)
  const [isSaveClickedBeforePhoneValidation, setIsSaveClickedBeforePhoneValidation] = useState(false)
  const [isEmergencyContactChanged, setIsEmergencyConatactChanged] = useState(false)
  const [isAddressChanged, setIsAddressChanged] = useState(false)
  const [isInitialEmergencyNumber, setIsInitialEmergencyNumber] = useState(true)

  const isPharmacyLoaded = useRef(false)

  const accountInfoConfig = useMemo(() => getAccountInfoConfig(currentPatientInfo), [currentPatientInfo])

  useEffect(() => {
    isPharmacyLoaded.current = false
    currentPatientInfo.Pharmacy = undefined
  }, [])

  useEffect(() => {
    if (currentPatientInfo?.Pharmacy !== undefined) {
      isPharmacyLoaded.current = true
    }
  }, [currentPatientInfo?.Pharmacy])

  useEffect(() => {
    if (!isEditMode) {
      dispatchContactInfo(
        setContactInfoState({
          pharmacyLocalError: '',
        }),
      )
    }
  }, [isEditMode])

  useEffect(() => {
    setIsInitialEmergencyNumber(
      stateEmergencyInfo.phoneCountryCode === currentPatientInfo?.EmergencyContact?.PhoneNumber?.Prefix?.slice(1) &&
        stateEmergencyInfo.phoneNumber === getPhoneNumberValue(currentPatientInfo?.EmergencyContact?.PhoneNumber),
    )
    if (
      stateContactInfo.forcePhoneLocalError ===
        getUserFriendlyErrorMessage(errorCodes.PHONE_NUMBER_CONNOT_BE_AS_EMERGENCY) ||
      stateEmergencyInfo.forcePhoneLocalError ===
        getUserFriendlyErrorMessage(errorCodes.PHONE_NUMBER_CONNOT_MATCH_YOUR_OWN)
    ) {
      dispatchContactInfo(
        setContactInfoState({
          phoneLocalError: '',
          forcePhoneLocalError: '',
        }),
      )
      dispatchEmergencyInfo(
        setEmergencyInfoState({
          phoneLocalError: '',
          forcePhoneLocalError: '',
        }),
      )
    }
  }, [
    stateContactInfo.phoneNumber,
    stateContactInfo.phoneCountryCode,
    stateEmergencyInfo.phoneNumber,
    stateEmergencyInfo.phoneCountryCode,
  ])
  useEffect(() => {
    dispatch(setPhoneValidation(null, id))
    dispatchContactInfo(
      setContactInfoState({
        phoneLocalError: '',
        forcePhoneLocalError: '',
      }),
    )
  }, [stateContactInfo.phoneNumber, stateContactInfo.phoneCountryCode])

  useEffect(() => {
    if (Object.keys(currentPatientInfo || {}).length > 1 && !isEditMode) {
      const {
        FirstName,
        LastName,
        PreferredName,
        Address,
        PhoneNumber,
        EmergencyContact,
        Pharmacy,
        BirthDate,
        AccountStatus,
      } = currentPatientInfo
      const initialPersonalData = {
        firstName: FirstName,
        lastName: LastName,
        prefferedName: PreferredName,
        dateOfBirthValue: moment(BirthDate).format(DATE_FORMAT_SLASH),
      }
      dispatchPersonalInfo(
        setPersonalInfoState({
          ...initialPersonalData,
        }),
      )
      const initialAddress = {
        streetAddress1: Address?.StreetAddress1 || '',
        streetAddress2: Address?.StreetAddress2 || '',
        city: Address?.City || '',
        stateCode: Address?.StateCode || '',
        zipCode: Address?.Zip || '',
        pharmacyValue: setPharmacyAddressValue(Pharmacy),
        pharmacyValueBeforeInputBlured: setPharmacyAddressValue(Pharmacy),
        selectedPharmacyValue: setPharmacyAddressValue(Pharmacy),
        isAddressValid: AccountStatus !== ACCOUNT_STATUS_TREAT_ERROR ? Address : false,
      }

      dispatchContactInfo(setContactInfoState({ ...initialAddress }))

      if (PhoneNumber) {
        const phoneInitialData = {
          isPhoneCorrect: true,
          isOldNumberCorrect: true,
          phoneCountry: PhoneNumber?.CountryCode?.toLowerCase(),
          phoneCountryCode: PhoneNumber?.Prefix?.slice(1),
          phoneNumber: getPhoneNumberValue(PhoneNumber),
        }
        dispatchContactInfo(setContactInfoState({ ...phoneInitialData }))
      }
      if (currentPatientInfo) {
        dispatchContactInfo(setContactInfoState({ emailAddress: currentPatientInfo?.Email }))
      }
      if (EmergencyContact) {
        const initialEmergencyData = {
          firstName: EmergencyContact?.FirstName,
          lastName: EmergencyContact?.LastName,
          relationship: EmergencyContact?.Relationship,
        }
        dispatchEmergencyInfo(setEmergencyInfoState({ ...initialEmergencyData }))
        if (EmergencyContact?.PhoneNumber) {
          const initialEmergencyPhoneData = {
            isPhoneCorrect: true,
            isOldNumberCorrect: true,
            phoneCountry: EmergencyContact?.PhoneNumber?.CountryCode?.toLowerCase(),
            phoneCountryCode: EmergencyContact?.PhoneNumber?.Prefix?.slice(1),
            phoneNumber: getPhoneNumberValue(EmergencyContact?.PhoneNumber),
            phoneLocalError: '',
            forcePhoneLocalError: '',
          }
          dispatchEmergencyInfo(
            setEmergencyInfoState({
              ...initialEmergencyPhoneData,
            }),
          )
        }
      } else {
        dispatchEmergencyInfo(
          setEmergencyInfoState({
            ...initialEmergencyInfoState,
          }),
        )
      }
    }
  }, [currentPatientInfo, isEditMode])

  useEffect(() => {
    dispatchContactInfo(
      setContactInfoState({
        pharmacyValue: setPharmacyAddressValue(currentPatientInfo?.Pharmacy),
        pharmacyValueBeforeInputBlured: setPharmacyAddressValue(currentPatientInfo?.Pharmacy),
        selectedPharmacyValue: setPharmacyAddressValue(currentPatientInfo?.Pharmacy),
      }),
    )
  }, [currentPatientInfo?.Pharmacy])

  useEffect(() => {
    if (isAccountUpdatedSuccess) {
      setIsEditMode(false)
      setIsSaveClickedBeforePhoneValidation(false)
      setIsAddressChanged(false)
      dispatchPersonalInfo(
        setPersonalInfoState({
          isAccountInfoChanged: false,
        }),
      )
      dispatchContactInfo(
        setContactInfoState({
          isPhoneChanged: false,
          isPhoneCorrect: false,
          selectedPharmacyId: null,
          isOldNumberCorrect: '',
        }),
      )
      dispatchEmergencyInfo(
        setEmergencyInfoState({
          isPhoneChanged: false,
          isPhoneCorrect: false,
          isOldNumberCorrect: '',
          phoneLocalError: '',
          forcePhoneLocalError: '',
        }),
      )
      // @ts-expect-error TS2554 -- MIGRATION: not approved, fix when encountered
      dispatch(setIsAccountUpdatedSuccess())
    }
  }, [isAccountUpdatedSuccess])

  useEffect(() => {
    if (
      stateEmergencyInfo?.firstName?.trim() !== (currentPatientInfo?.EmergencyContact?.FirstName || '') ||
      stateEmergencyInfo?.lastName?.trim() !== (currentPatientInfo?.EmergencyContact?.LastName || '') ||
      stateEmergencyInfo?.relationship?.trim() !== (currentPatientInfo?.EmergencyContact?.Relationship || '') ||
      stateEmergencyInfo?.phoneNumber?.trim() !==
        getPhoneNumberValue(currentPatientInfo?.EmergencyContact?.PhoneNumber) ||
      stateEmergencyInfo?.phoneCountryCode?.trim() !==
        (currentPatientInfo?.EmergencyContact?.PhoneNumber?.Prefix?.slice(1) || '1')
    ) {
      setIsEmergencyConatactChanged(true)
    } else setIsEmergencyConatactChanged(false)
  }, [
    stateEmergencyInfo.firstName,
    stateEmergencyInfo.lastName,
    stateEmergencyInfo.relationship,
    stateEmergencyInfo.phoneNumber,
    stateEmergencyInfo.phoneCountryCode,
    patientInfo,
  ])

  const checkEmptyPatientDataRequieredFields = () => {
    const patientDataFieldsForCheckConfig = [
      {
        errorMessageExtraValue: FIRST_NAME_TEXT,
        isFieldEmpty: !statePersonalInfo?.firstName?.trim(),
        fieldErrorKey: 'firstNameLocalError',
        reducerType: PERSONAL_INFO_REDUCER_TYPE,
      },
      {
        errorMessageExtraValue: LAST_NAME_TEXT,
        isFieldEmpty: !statePersonalInfo?.lastName?.trim(),
        fieldErrorKey: 'lastNameLocalError',
        reducerType: PERSONAL_INFO_REDUCER_TYPE,
      },
      {
        errorMessageExtraValue: INTAKE_FORM_TYPE_PHONE_NUMBER_TEXT,
        isFieldEmpty: !stateContactInfo?.phoneNumber?.trim(),
        fieldErrorKey: 'phoneLocalError',
        reducerType: CONTACT_INFO_REDUCER_TYPE,
      },
      {
        errorMessageExtraValue: STREET_ADDRESS_1_TEXT,
        isFieldEmpty: !stateContactInfo?.streetAddress1?.trim(),
        fieldErrorKey: 'streetAddress1LocalError',
        reducerType: CONTACT_INFO_REDUCER_TYPE,
      },
      {
        errorMessageExtraValue: CITY_TEXT,
        isFieldEmpty: !stateContactInfo?.city?.trim(),
        fieldErrorKey: 'cityLocalError',
        reducerType: CONTACT_INFO_REDUCER_TYPE,
      },
      {
        errorMessageExtraValue: EMAIL_ADDRESS_TEXT,
        isFieldEmpty: !stateContactInfo?.emailAddress?.trim(),
        fieldErrorKey: 'emailAddressLocalError',
        reducerType: CONTACT_INFO_REDUCER_TYPE,
      },
      {
        errorMessageExtraValue: STATE_TEXT,
        isFieldEmpty: !stateContactInfo?.stateCode?.trim(),
        fieldErrorKey: 'stateCodeLocalError',
        reducerType: CONTACT_INFO_REDUCER_TYPE,
      },
      {
        errorMessageExtraValue: ZIP_CODE_TEXT,
        isFieldEmpty: !stateContactInfo?.zipCode?.trim(),
        fieldErrorKey: 'zipCodeLocalError',
        reducerType: CONTACT_INFO_REDUCER_TYPE,
      },
    ]

    const patientDataEmptyErrorsConfig = patientDataFieldsForCheckConfig.reduce(
      (acc, { errorMessageExtraValue, isFieldEmpty, fieldErrorKey, reducerType }) => {
        if (isFieldEmpty) {
          // @ts-expect-error TS7053 -- MIGRATION: not approved, fix when encountered
          acc[reducerType] = {
            // @ts-expect-error TS7053 -- MIGRATION: not approved, fix when encountered
            ...acc[reducerType],
            [fieldErrorKey]: getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, errorMessageExtraValue),
          }
        }

        return acc
      },
      {},
    )

    Object.keys(patientDataEmptyErrorsConfig).forEach((reducerType) => {
      if (reducerType === PERSONAL_INFO_REDUCER_TYPE) {
        dispatchPersonalInfo(setPersonalInfoState(patientDataEmptyErrorsConfig[reducerType]))
      } else {
        dispatchContactInfo(setContactInfoState(patientDataEmptyErrorsConfig[reducerType]))
      }
    })
  }

  const checkEmptyEmergencyContactFields = () => {
    const emergencyContactFieldsForCheckConfig = [
      {
        isFieldEmpty: !stateEmergencyInfo?.firstName?.trim(),
        fieldErrorKey: 'firstNameLocalError',
      },
      {
        isFieldEmpty: !stateEmergencyInfo?.lastName?.trim(),
        fieldErrorKey: 'lastNameLocalError',
      },
      {
        isFieldEmpty: !stateEmergencyInfo?.relationship?.trim(),
        fieldErrorKey: 'relationshipLocalError',
      },
      {
        isFieldEmpty: !stateEmergencyInfo?.phoneNumber?.trim(),
        fieldErrorKey: 'phoneLocalError',
      },
    ]

    const emergencyContactEmptyErrorsList = emergencyContactFieldsForCheckConfig.reduce(
      (acc, { isFieldEmpty, fieldErrorKey }) => {
        if (isFieldEmpty) {
          // @ts-expect-error TS7053 -- MIGRATION: not approved, fix when encountered
          acc[fieldErrorKey] = getUserFriendlyErrorMessage(
            errorCodes.ALL_FIELDS_MUST_BE_POPULATED,
            null,
            EMERGENCY_CONTACT_TEXT,
          )
        }

        return acc
      },
      {},
    )

    dispatchEmergencyInfo(setEmergencyInfoState(emergencyContactEmptyErrorsList))
  }

  // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
  const checkValidationInputs = (zipCodeIsValid, DOBIsValid) => {
    const fieldsForCheckValidationConfig = [
      {
        errorMessageExtraValue: FIRST_NAME_TEXT.toLocaleLowerCase(),
        isFieldNotValid: !statePersonalInfo?.firstName?.match(stringHasLatterRegexp),
        fieldErrorKey: 'firstNameLocalError',
        reducerType: PERSONAL_INFO_REDUCER_TYPE,
      },
      {
        errorMessageExtraValue: LAST_NAME_TEXT.toLocaleLowerCase(),
        isFieldNotValid: !statePersonalInfo?.lastName?.match(stringHasLatterRegexp),
        fieldErrorKey: 'lastNameLocalError',
        reducerType: PERSONAL_INFO_REDUCER_TYPE,
      },
      {
        errorMessageExtraValue: 'date of birth',
        isFieldNotValid: !DOBIsValid,
        fieldErrorKey: 'dateOfBirthLocalError',
        reducerType: PERSONAL_INFO_REDUCER_TYPE,
      },
      {
        errorMessageExtraValue: PREFERRED_PHARMACY_TEXT.toLocaleLowerCase(),
        isFieldNotValid: stateContactInfo?.selectedPharmacyValue !== stateContactInfo?.pharmacyValue,
        fieldErrorKey: 'pharmacyLocalError',
        reducerType: CONTACT_INFO_REDUCER_TYPE,
      },
      {
        errorMessageExtraValue: INVALID_ZIP_CODE_ERROR_TEXT,
        isFieldNotValid: isAddressChanged && !zipCodeIsValid,
        fieldErrorKey: 'zipCodeLocalError',
        reducerType: CONTACT_INFO_REDUCER_TYPE,
      },
    ]

    const fieldsValidationErrorsConfig = fieldsForCheckValidationConfig.reduce(
      (acc, { errorMessageExtraValue, isFieldNotValid, fieldErrorKey, reducerType }) => {
        if (isFieldNotValid) {
          if (fieldErrorKey === 'pharmacyLocalError') {
            dispatchContactInfo(
              setContactInfoState({
                pharmacyValue: setPharmacyAddressValue(currentPatientInfo?.Pharmacy),
                selectedPharmacyValue: setPharmacyAddressValue(currentPatientInfo?.Pharmacy),
              }),
            )
          }
          // @ts-expect-error TS7053 -- MIGRATION: not approved, fix when encountered
          acc[reducerType] = {
            // @ts-expect-error TS7053 -- MIGRATION: not approved, fix when encountered
            ...acc[reducerType],
            [fieldErrorKey]: getUserFriendlyErrorMessage(errorCodes.ENTER_VALID, null, errorMessageExtraValue),
          }
        }

        return acc
      },
      {},
    )

    Object.keys(fieldsValidationErrorsConfig).forEach((reducerType) => {
      if (reducerType === PERSONAL_INFO_REDUCER_TYPE) {
        dispatchPersonalInfo(setPersonalInfoState(fieldsValidationErrorsConfig[reducerType]))
      } else {
        dispatchContactInfo(setContactInfoState(fieldsValidationErrorsConfig[reducerType]))
      }
    })

    dispatch(setChartsError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS)))
  }

  const onClickAccountInfoBtn = () => {
    if (isEditMode) {
      if (
        ((stateEmergencyInfo?.isPhoneChanged && stateEmergencyInfo?.phoneNumber.trim()) ||
          (stateContactInfo?.isPhoneChanged && stateContactInfo?.phoneNumber.trim())) &&
        isPhoneValidationInProgress
      ) {
        setIsSaveClickedBeforePhoneValidation(true)
        return
      }

      const hasPatientDataEmptyError =
        !statePersonalInfo?.firstName?.trim() ||
        !statePersonalInfo?.lastName?.trim() ||
        !stateContactInfo?.streetAddress1?.trim() ||
        !stateContactInfo?.city?.trim() ||
        !stateContactInfo?.stateCode?.trim() ||
        !stateContactInfo?.zipCode?.trim() ||
        !stateContactInfo?.phoneNumber?.trim() ||
        !stateContactInfo?.emailAddress?.trim()

      const hasEmergencyContactDataFilledInput =
        stateEmergencyInfo?.firstName?.trim() ||
        stateEmergencyInfo?.lastName?.trim() ||
        stateEmergencyInfo?.relationship?.trim() ||
        stateEmergencyInfo?.phoneNumber?.trim()

      const hasEmergencyContacyDataEmptyError =
        hasEmergencyContactDataFilledInput &&
        (!stateEmergencyInfo?.firstName?.trim() ||
          !stateEmergencyInfo?.lastName?.trim() ||
          !stateEmergencyInfo?.relationship?.trim() ||
          !stateEmergencyInfo?.phoneNumber?.trim())

      const isPhoneNumbersTheSame =
        stateEmergencyInfo?.phoneNumber?.trim() === stateContactInfo?.phoneNumber?.trim() &&
        stateContactInfo?.phoneCountryCode === stateEmergencyInfo?.phoneCountryCode

      if (hasPatientDataEmptyError || (isEmergencyContactChanged && hasEmergencyContacyDataEmptyError)) {
        if (hasPatientDataEmptyError) {
          checkEmptyPatientDataRequieredFields()
        }

        if (isEmergencyContactChanged && hasEmergencyContacyDataEmptyError) {
          checkEmptyEmergencyContactFields()
        }

        dispatch(setChartsError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS)))
        return
      }
      if (stateContactInfo?.emailAddress?.trim()?.length && !validateEmail(stateContactInfo?.emailAddress.trim())) {
        dispatchContactInfo(
          setContactInfoState({
            emailAddressLocalError: getUserFriendlyErrorMessage(errorCodes.ENTER_VALID, null, 'email'),
          }),
        )
        dispatch(setChartsError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS)))
        return
      }

      const zipCodeIsValid = zipCodeRegexp.exec(stateContactInfo?.zipCode)

      // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
      const DOBValuesList = statePersonalInfo?.dateOfBirthValue?.split('/')?.filter((value) => value)
      const monthDOBValue = DOBValuesList?.[0]
      const dateDOBValue = DOBValuesList?.[1]
      const yearDOBValue = DOBValuesList?.[2]

      const DOBIsValid =
        !(DOBValuesList?.length < 3) &&
        moment(`${dateDOBValue}-${monthDOBValue}-${yearDOBValue}`, 'DD-MM-YYYY').isValid() &&
        !(yearDOBValue?.length < 4) &&
        !moment(`${dateDOBValue}-${monthDOBValue}-${yearDOBValue}`, 'DD-MM-YYYY').isAfter(new Date()) &&
        !moment(`${dateDOBValue}-${monthDOBValue}-${yearDOBValue}`, 'DD-MM-YYYY').isBefore('1901-01-01', 'year')

      const isNeedToCheckEmergencyContactPhoneInputWithError =
        stateEmergencyInfo?.phoneLocalError &&
        stateEmergencyInfo?.phoneLocalError !==
          getUserFriendlyErrorMessage(errorCodes.ALL_FIELDS_MUST_BE_POPULATED, null, EMERGENCY_CONTACT_TEXT)

      const isNeedToCheckValidationInputs =
        stateContactInfo?.phoneLocalError ||
        isNeedToCheckEmergencyContactPhoneInputWithError ||
        stateContactInfo?.pharmacyValue !== stateContactInfo?.selectedPharmacyValue ||
        !statePersonalInfo?.firstName?.match(stringHasLatterRegexp) ||
        !statePersonalInfo?.lastName?.match(stringHasLatterRegexp) ||
        !DOBIsValid ||
        (isAddressChanged && !zipCodeIsValid)

      if (isNeedToCheckValidationInputs) {
        checkValidationInputs(zipCodeIsValid, DOBIsValid)
        dispatch(setChartsError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS)))
        return
      }

      if (isPhoneNumbersTheSame) {
        dispatchContactInfo(
          setContactInfoState({
            phoneLocalError: getUserFriendlyErrorMessage(errorCodes.PHONE_NUMBER_CONNOT_BE_AS_EMERGENCY),
          }),
        )
        dispatchEmergencyInfo(
          setEmergencyInfoState({
            phoneLocalError: getUserFriendlyErrorMessage(errorCodes.PHONE_NUMBER_CONNOT_MATCH_YOUR_OWN),
          }),
        )

        dispatch(setChartsError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS)))
        return
      }

      const ageValue = moment().diff(moment(`${dateDOBValue}-${monthDOBValue}-${yearDOBValue}`, 'DD-MM-YYYY'), 'years')

      if (ageValue < 18 && (!currentPatientInfo?.HeightInches || !currentPatientInfo?.WeightPounds)) {
        setIsGeneralInfoEditMode(true)
        return
      }
      if (stateContactInfo.emailAddressLocalError) {
        return
      }
      if (
        statePersonalInfo?.isAccountInfoChanged ||
        isAddressChanged ||
        stateEmergencyInfo?.isPhoneChanged ||
        stateContactInfo?.isPhoneChanged ||
        isEmergencyContactChanged ||
        stateContactInfo?.selectedPharmacyId ||
        currentPatientInfo.Email.trim() !== stateContactInfo?.emailAddress.trim()
      ) {
        if (stateEmergencyInfo?.isResetEmergencyContact) {
          dispatchEmergencyInfo(
            setEmergencyInfoState({
              phoneCountry: 'us',
              phoneCountryCode: '1',
            }),
          )
        }
        const newEmergencyContactData =
          stateEmergencyInfo?.isResetEmergencyContact ? null : (
            {
              FirstName: stateEmergencyInfo?.firstName,
              LastName: stateEmergencyInfo?.lastName,
              Relationship: stateEmergencyInfo?.relationship,
              PhoneNumber: {
                CountryCode: stateEmergencyInfo?.phoneCountry?.toUpperCase(),
                Prefix: `+${stateEmergencyInfo?.phoneCountryCode}`,
                Number: `+${stateEmergencyInfo?.phoneCountryCode}${stateEmergencyInfo?.phoneNumber}`,
              },
            }
          )

        const newAddressData =
          isAddressChanged ?
            {
              StreetAddress1: stateContactInfo?.streetAddress1,
              StreetAddress2: stateContactInfo?.streetAddress2,
              City: stateContactInfo?.city,
              Zip: stateContactInfo?.zipCode,
              StateCode: stateContactInfo?.stateCode,
              CountryCode: 'US',
            }
          : null

        const newAccountInfoConfig = {
          FirstName: statePersonalInfo?.firstName,
          LastName: statePersonalInfo?.lastName,
          PreferredName: statePersonalInfo?.prefferedName,
          PharmacyId: stateContactInfo?.selectedPharmacyId,
          BirthDate: `${yearDOBValue}-${monthDOBValue}-${dateDOBValue}`,
          PhoneNumber:
            stateContactInfo?.isPhoneChanged ?
              {
                CountryCode: stateContactInfo?.phoneCountry?.toUpperCase(),
                Prefix: `+${stateContactInfo?.phoneCountryCode}`,
                Number: `+${stateContactInfo?.phoneCountryCode}${stateContactInfo?.phoneNumber}`,
              }
            : null,
          Address: newAddressData,
          Email: isCanEditEmail ? stateContactInfo?.emailAddress.trim() : null,
          EmergencyContact: newEmergencyContactData,
        }
        dispatch(
          // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
          updateAccountInfo({
            patientId: id,
            newAccountInfo: newAccountInfoConfig,
            newPharmacyInfo:
              stateContactInfo.selectedPharmacyConfig ?
                modifySelectedPharmacyConfigForUpdate(stateContactInfo.selectedPharmacyConfig)
              : null,
            mediaFiles,
          }),
        )
      } else {
        setIsEditMode((prev) => !prev)
      }
    } else {
      setIsEditMode((prev) => !prev)
    }
  }

  useEffect(() => {
    if (phoneIsValid !== null) {
      if (stateContactInfo?.isPhoneValidation) {
        setIsPhoneValidationInProgress(false)
        dispatchContactInfo(
          setContactInfoState({
            isPhoneValidation: false,
            isPhoneCorrect: phoneIsValid,
            isOldNumberCorrect: phoneIsValid,
            phoneLocalError:
              phoneIsValid === false ? getUserFriendlyErrorMessage(errorCodes.ENTER_VALID, null, 'phone number') : '',
          }),
        )
      }

      if (stateEmergencyInfo?.isPhoneValidation) {
        setIsPhoneValidationInProgress(false)
        dispatchEmergencyInfo(
          setEmergencyInfoState({
            isPhoneValidation: false,
            isPhoneCorrect: phoneIsValid,
            isOldNumberCorrect: phoneIsValid,
            phoneLocalError:
              !phoneIsValid ? getUserFriendlyErrorMessage(errorCodes.ENTER_VALID, null, 'phone number') : '',
          }),
        )
      }
      if (isSaveClickedBeforePhoneValidation) {
        if (phoneIsValid) {
          dispatchContactInfo(
            setContactInfoState({
              isPhoneChanged: false,
            }),
          )
          dispatchEmergencyInfo(
            setEmergencyInfoState({
              isPhoneChanged: false,
            }),
          )
          onClickAccountInfoBtn()
        } else {
          setIsSaveClickedBeforePhoneValidation(false)
        }
      }
    }
  }, [phoneIsValid])
  return (
    // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
    <ChartSection
      className='account-info-chart-section'
      title={ACCOUNT_INFORMATION_TEXT}
      icon={accountInfoIcon}
      iconAltText='account-info'
      setIsCollapsed={setIsCollapsed}
      isCollapsed={isCollapsed}
      clickButtonAction={onClickAccountInfoBtn}
      isEditMode={isEditMode}
      userTimeZone={userTimeZone}
      isWithoutLastUpdated
      isShowButton={!isMergedAccount}>
      {isEditMode ?
        <>
          {/*
           // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered */}
          <EditPersonalInfoBlock stateInfo={statePersonalInfo} dispatchInfo={dispatchPersonalInfo} />

          <EditContactInfoBlock
            // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
            stateInfo={stateContactInfo}
            dispatchInfo={dispatchContactInfo}
            setIsPhoneValidationInProgress={setIsPhoneValidationInProgress}
            setIsAddressChanged={setIsAddressChanged}
            isSaveClickedBeforePhoneValidation={isSaveClickedBeforePhoneValidation}
            isPhoneValidationInProgress={isPhoneValidationInProgress}
            isAlowEditPhoneNumber
          />

          <EditEmergencyInfoBlock
            // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
            stateInfo={stateEmergencyInfo}
            dispatchInfo={dispatchEmergencyInfo}
            setIsPhoneValidationInProgress={setIsPhoneValidationInProgress}
            isSaveClickedBeforePhoneValidation={isSaveClickedBeforePhoneValidation}
            isPhoneValidationInProgress={isPhoneValidationInProgress}
            isInitialEmergencyNumber={isInitialEmergencyNumber}
          />
        </>
      : accountInfoConfig.map((infoCardItemConfig) => (
          // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
          <InfoCard key={infoCardItemConfig.title} infoConfig={infoCardItemConfig} />
        ))
      }

      <AccountCreditsBlock />
    </ChartSection>
  )
})
